import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, LogOut, User, FileText, Folder, CheckSquare, ChevronLeft, ChevronRight } from "react-feather";

import { getbranchData, handleLogout } from "../../../../../redux/authentication";
import {
  getInventoryItems,
  getInventoryPurchase,
  getInventorySupplier,
  handleError,
} from "../../../../../redux/inventory";

// Framer Motion layout configuration variants
const layoutVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: "easeOut", staggerChildren: 0.08 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.25 } }
};

// Sidebar animation panel variants
const sidebarVariants = {
  open: {
    width: "auto",
    opacity: 1,
    x: 0,
    transition: { type: "spring", stiffness: 300, damping: 30 }
  },
  closed: {
    width: 0,
    opacity: 0,
    x: -20,
    transition: { type: "spring", stiffness: 300, damping: 30 }
  }
};

const Lab = () => {
  const itemsList = useSelector((state) => state.itemsList);
  const auth = useSelector((state) => state.authentication);
  const dispatch = useDispatch();
  const { userData, branchData } = auth;
  const navigate = useNavigate();

  // State to manage side panel collapse status
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    if (userData.token) {
      dispatch(
        getInventoryPurchase({
          token: userData.token,
          branch: userData.branch,
        })
      );
      dispatch(
        getInventorySupplier({
          token: userData.token,
          branch: userData.branch,
        })
      );
      dispatch(
        getInventoryItems({ token: userData.token, branch: userData.branch })
      );
    }
  }, [userData.token]);

  useEffect(() => {
    if (itemsList) {
      if (
        itemsList.error &&
        itemsList.error.message === "Request failed with status code 403"
      ) {
        dispatch(handleError());
        dispatch(handleLogout());
        navigate("/");
      }
    }
  }, [itemsList]);

  return (
    <Container fluid className="py-4 bg-light min-vh-100">
      <motion.div variants={layoutVariants} initial="hidden" animate="visible">
        <Card className="border-0 shadow-sm rounded-3 overflow-hidden mb-5">
          
          {/* Glassmorphism Header Bar */}
          <Card.Header
            className="text-white p-4 border-0"
            style={{
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              backgroundColor: "rgba(50, 0, 100, 0.85)",
            }}
          >
            <Row className="align-items-center">
              <Col md={4} xs={6} className="d-flex align-items-center">
                <div className="bg-white bg-opacity-20 p-2 rounded-2 me-3 d-none d-sm-block">
                  <CheckSquare size={20} />
                </div>
                <h4 className="mb-0 fw-bold tracking-tight">Lab Management</h4>
              </Col>
              <Col md={4} className="d-none d-md-block text-center text-white text-opacity-75 small">
                Clinical Pathology Diagnostic Desk
              </Col>
              <Col md={4} xs={6} className="text-end">
                <div className="d-inline-flex align-items-center gap-2 bg-white bg-opacity-10 px-3 py-1.5 rounded-pill border border-white border-opacity-10">
                  <User size={14} className="text-white text-opacity-75" />
                  <span className="small">
                    <span className="text-white text-opacity-50 me-1">User:</span>
                    <strong>{userData.user}</strong>
                  </span>
                </div>
              </Col>
            </Row>
          </Card.Header>

          {/* Functional Actions Banner Line */}
          <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-white border-bottom gap-2">
            <div className="d-flex align-items-center gap-2">
              {(userData.rights === "Admin" ||
                userData.rights === "Manager" ||
                userData.rights === "Doctor") && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="d-inline-flex align-items-center gap-2 px-3 rounded-2 bg-white shadow-sm"
                  onClick={() => {
                    if (userData.rights === "Doctor") {
                      navigate("/trtmnt");
                      return;
                    }
                    navigate("/crm-clt");
                  }}
                >
                  <ArrowLeft size={14} /> Back
                </Button>
              )}

              {/* Sidebar toggle controller button */}
              <Button
                variant="light"
                size="sm"
                className="d-inline-flex align-items-center gap-2 px-2.5 rounded-2 border shadow-sm"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                title={isSidebarOpen ? "Collapse Navigation Menu" : "Expand Navigation Menu"}
              >
                {isSidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                <span className="small d-none d-sm-inline">{isSidebarOpen ? "Hide Menu" : "Show Menu"}</span>
              </Button>
            </div>
            
            <Button
              variant="outline-danger"
              size="sm"
              className="d-inline-flex align-items-center gap-2 px-3 rounded-2 bg-white shadow-sm"
              onClick={() => {
                sessionStorage.removeItem("token");
                sessionStorage.removeItem("srchData");
                sessionStorage.removeItem("prescriptionHistory");
                sessionStorage.removeItem("rights");
                dispatch(handleLogout());
                navigate("/cri-crm");
              }}
            >
              <LogOut size={14} /> Log Out
            </Button>
          </div>

          <Card.Body className="p-4 bg-white">
            <Row className="g-4 flex-nowrap overflow-hidden">
              
              {/* Animated Left Segment Navigation Bar Panel */}
              <AnimatePresence initial={false}>
                {isSidebarOpen && (
                  <Col 
                    xs="auto"
                    as={motion.div}
                    variants={sidebarVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                  >
                    <div className="d-flex flex-column gap-2 border-end pe-3 h-100" style={{ width: "240px" }}>
                      
                      <motion.div variants={itemVariants}>
                        <Button
                          variant="primary"
                          className="w-100 py-2.5 text-start px-3 d-flex align-items-center gap-2 rounded-3 shadow-sm fw-medium"
                          onClick={() => navigate("/lab-admin/lab-report")}
                        >
                          <FileText size={16} /> Prepare Lab Report
                        </Button>
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <Button
                          variant="outline-primary"
                          className="w-100 py-2.5 text-start px-3 d-flex align-items-center gap-2 rounded-3 bg-white border border-light-subtle text-secondary"
                          onClick={() => navigate("/lab-admin/lab-cat")}
                        >
                          <Folder size={16} className="text-primary" /> Category Setup
                        </Button>
                      </motion.div>

                      <motion.div variants={itemVariants}>
                        <Button
                          variant="outline-primary"
                          className="w-100 py-2.5 text-start px-3 d-flex align-items-center gap-2 rounded-3 bg-white border border-light-subtle text-secondary"
                          onClick={() => navigate("/lab-admin/lab-results")}
                        >
                          <CheckSquare size={16} className="text-primary" /> Analysis Results
                        </Button>
                      </motion.div>

                    </div>
                  </Col>
                )}
              </AnimatePresence>

              {/* Right Workspace Dynamic View Injection Port */}
              <Col className="flex-grow-1 min-w-0">
                <div className="p-1 bg-transparent rounded h-100">
                  <Outlet />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </motion.div>

      {/* Intact Structural Shell Modal Layer */}
      <Modal show={false} centered className="border-0">
        <Modal.Header closeButton className="bg-light border-0">
          <Modal.Title className="h6 mb-0 fw-bold"></Modal.Title>
        </Modal.Header>
      </Modal>
    </Container>
  );
};

export default Lab;